import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { STORAGE_KEYS } from '@/data/constants'

type Theme = 'light' | 'dark' | 'system'

interface ThemeState {
  theme: Theme
  currentTheme: 'light' | 'dark'
}

interface ThemeActions {
  setTheme: (theme: Theme) => void
  applyTheme: () => void
  toggleTheme: () => void
}

type ThemeStore = ThemeState & ThemeActions

const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      theme: 'system',
      currentTheme: 'light',
      
      setTheme: (theme) => {
        set({ theme })
        get().applyTheme()
      },
      
      toggleTheme: () => {
        const { currentTheme } = get()
        const newTheme = currentTheme === 'light' ? 'dark' : 'light'
        set({ theme: newTheme })
        get().applyTheme()
      },
      
      applyTheme: () => {
        if (typeof window === 'undefined') return
        
        const { theme } = get()
        const root = window.document.documentElement
        
        root.classList.remove('light', 'dark')
        
        if (theme === 'system') {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
            .matches
            ? 'dark'
            : 'light'
          
          root.classList.add(systemTheme)
          set({ currentTheme: systemTheme })
          return
        }
        
        root.classList.add(theme)
        set({ currentTheme: theme as 'light' | 'dark' })
      },
    }),
    {
      name: STORAGE_KEYS.THEME,
    }
  )
)

// Apply theme on store initialization
useThemeStore.getState().applyTheme()

export default useThemeStore

// Convenience hook
export const useTheme = () => useThemeStore()